/**
 * Integration ontology — bespoke at every level.
 *
 * Derived from the Fern API spec's IntegrationSource enum:
 *   agentmail, nim, stripe, notion, posthog, cloudflare, vercel
 *
 * But each integration is NOT a flat entry. Each has:
 *   - Its own role in the workflow (which levels it serves)
 *   - Its own surface posture (where it's visible, where it's managed)
 *   - Its own lifecycle (provisioning → active → health → failure → recovery)
 *   - Its own configuration model (bespoke fields, not a generic key-value)
 *   - Its own health surface (what "healthy" means for THIS integration)
 *   - Its own failure semantics (what breaks, how it recovers, what the client sees)
 *
 * The integrations tab is a bulwark — it keeps us scoped and lets us deliver.
 * It has its own ontology. Not the moat, not the whole product, but essential.
 * Required on every build. 1% of what we do, but the 1% that everything else stands on.
 *
 * Envoy is ahead of all the other APIs in terms of being a complete architecture.
 * But we've built far ahead of just Envoy — upstream, downstream, and in between.
 */

import type { SurfaceType, WorkflowLevel, SurfaceCapability } from "./surface-context";

export type IntegrationSource =
  | "agentmail"
  | "nim"
  | "stripe"
  | "notion"
  | "posthog"
  | "cloudflare"
  | "vercel";

export type IntegrationStatus =
  | "unprovisioned"  // not set up yet
  | "provisioning"   // in progress
  | "active"         // healthy and working
  | "degraded"       // working but with issues
  | "failed"         // broken, needs attention
  | "paused";       // intentionally off

export type IntegrationLifecycle =
  | "discovery"     // the user is learning about this
  | "setup"         // configuring it
  | "running"       // it's doing its job
  | "monitoring"    // we're watching it
  | "intervention"  // needs human action
  | "retired";     // no longer needed

// ── Bespoke per-integration metadata ─────────────────────────────────────

export interface IntegrationMeta {
  source: IntegrationSource;
  /** Client-safe label (MembraneLanguage applied at render time) */
  label: string;
  description: string;
  category: IntegrationCategory;
  icon: string;
  /** Which Fern service definitions power this integration */
  services: string[];
  /** Workflow levels this integration serves */
  levels: WorkflowLevel[];
  /** Which surfaces can see/manage this integration */
  surfaces: {
    visible: SurfaceType[];
    managed: SurfaceType[];
  };
  /** What capabilities this integration enables */
  enables: SurfaceCapability[];
  /** Whether this is operator-managed vs client-managed vs both */
  managedBy: "client" | "operator" | "both";
  /** Health check type for this integration */
  healthCheck: HealthCheckType;
  /** The lifecycle this integration follows */
  lifecycle: IntegrationLifecycle;
  /** Whether this integration is required (must be active before others) */
  required: boolean;
  /** Dependencies — other integrations that must be active first */
  dependsOn?: IntegrationSource[];
  /** Bespoke config fields — each integration has its own shape */
  configModel: ConfigField[];
}

export type IntegrationCategory =
  | "email"      // agentmail: outbound sending, inbox provisioning, warmup
  | "ai"         // nim: LLM inference, agent reasoning
  | "payments"   // stripe: billing, credits, metering
  | "knowledge"  // notion: business context, agent memory
  | "analytics"  // posthog: event tracking, product instrumentation
  | "edge"       // cloudflare: workers, D1, R2, DNS
  | "deploy";   // vercel: frontend hosting

export type HealthCheckType =
  | "webhook"     // we receive webhooks from them
  | "poll"        // we poll their API
  | "cron"        // we run a scheduled job that depends on them
  | "passive"     // we know it's healthy because we use it (no explicit check)
  | "manual";     // operator checks manually

export interface ConfigField {
  key: string;
  label: string;
  type: "string" | "secret" | "select" | "boolean" | "list" | "json";
  required: boolean;
  description?: string;
  options?: string[]; // for select type
  placeholder?: string;
  /** Whether this field is visible to clients or operator-only */
  operatorOnly?: boolean;
  /** Default value if not set */
  default?: string | boolean | string[];
}

// ── The bespoke integration definitions ───────────────────────────────────

export const INTEGRATIONS: Record<IntegrationSource, IntegrationMeta> = {
  agentmail: {
    source: "agentmail",
    label: "Email Infrastructure",
    description: "Outbound email sending, inbox provisioning, warmup lifecycle, and webhook ingestion. Your agent's voice on email.",
    category: "email",
    icon: "✉",
    services: ["agentmail", "cold-email", "threads", "messages", "webhooks"],
    levels: [2, 3, 4],
    surfaces: {
      visible: ["browser", "desktop"],
      managed: ["browser"],
    },
    enables: ["campaigns", "offers"],
    managedBy: "both",
    healthCheck: "webhook",
    lifecycle: "running",
    required: true,
    configModel: [
      { key: "domains", label: "Sending domains", type: "list", required: true, description: "Domains approved for outbound email", operatorOnly: false },
      { key: "inboxes", label: "Inboxes", type: "list", required: true, description: "Provisioned email addresses for sending and receiving", operatorOnly: false },
      { key: "warmup_velocity", label: "Warmup velocity", type: "select", required: false, options: ["slow", "medium", "aggressive"], default: "medium", operatorOnly: true },
      { key: "dns_records", label: "DNS records", type: "json", required: true, description: "SPF, DKIM, DMARC records for each domain", operatorOnly: true },
      { key: "webhook_secret", label: "Webhook secret", type: "secret", required: true, operatorOnly: true },
    ],
  },

  nim: {
    source: "nim",
    label: "AI Model Service",
    description: "LLM inference powering agent reasoning, reply classification, and personalization. The brain behind your agent.",
    category: "ai",
    icon: "◆",
    services: ["agent", "classify", "personalize", "reply"],
    levels: [1, 2, 3, 4, 5],
    surfaces: {
      visible: ["browser", "desktop", "extension"],
      managed: ["browser"],
    },
    enables: ["chat", "terminal"],
    managedBy: "operator",
    healthCheck: "poll",
    lifecycle: "running",
    required: true,
    dependsOn: ["cloudflare"],
    configModel: [
      { key: "nim_model", label: "Model", type: "select", required: true, options: ["claude-sonnet-4-6", "claude-opus-4", "deepseek-pro", "deepseek-flash"], default: "claude-sonnet-4-6", operatorOnly: true },
      { key: "agent_mode", label: "Agent mode", type: "select", required: true, options: ["STEALTH", "ACTIVE", "AUTONOMOUS"], default: "STEALTH", operatorOnly: true },
      { key: "tools_enabled", label: "Tools enabled", type: "list", required: false, default: ["email", "calendar", "contacts"], operatorOnly: true },
      { key: "api_key", label: "NIM API key", type: "secret", required: true, operatorOnly: true },
    ],
  },

  stripe: {
    source: "stripe",
    label: "Payments & Billing",
    description: "Subscription billing, credit purchases, usage metering, and checkout flows. The financial rail.",
    category: "payments",
    icon: "▢",
    services: ["billing", "credits", "metering", "checkout"],
    levels: [2, 3, 4, 6],
    surfaces: {
      visible: ["browser", "desktop"],
      managed: ["browser"],
    },
    enables: ["offers", "conveyance"],
    managedBy: "both",
    healthCheck: "webhook",
    lifecycle: "running",
    required: true,
    configModel: [
      { key: "tier", label: "Plan tier", type: "select", required: true, options: ["free", "starter", "pro", "agency"], operatorOnly: false },
      { key: "checkout_url", label: "Checkout URL", type: "string", required: false, operatorOnly: true },
      { key: "credits_balance", label: "Credits balance", type: "string", required: false, operatorOnly: false },
      { key: "webhook_secret", label: "Webhook secret", type: "secret", required: true, operatorOnly: true },
      { key: "secret_key", label: "Secret key", type: "secret", required: true, operatorOnly: true },
    ],
  },

  notion: {
    source: "notion",
    label: "Knowledge Base",
    description: "Notion connection for agent memory, business context, and structured data queries. Your agent's notebook.",
    category: "knowledge",
    icon: "▦",
    services: ["notion", "databases", "pages"],
    levels: [1, 2, 4, 5],
    surfaces: {
      visible: ["browser", "extension"],
      managed: ["browser"],
    },
    enables: ["chat", "capture"],
    managedBy: "client",
    healthCheck: "poll",
    lifecycle: "setup",
    required: false,
    configModel: [
      { key: "auth_code", label: "Notion auth code", type: "secret", required: true, description: "Get this from Notion's OAuth flow", operatorOnly: false },
      { key: "database_id", label: "Database ID", type: "string", required: false, placeholder: "Optional — connect a specific database", operatorOnly: false },
      { key: "sync_frequency", label: "Sync frequency", type: "select", required: false, options: ["realtime", "hourly", "daily"], default: "hourly", operatorOnly: true },
    ],
  },

  posthog: {
    source: "posthog",
    label: "Product Analytics",
    description: "Event tracking, user analytics, and product instrumentation. Understanding what works.",
    category: "analytics",
    icon: "○",
    services: ["posthog", "events", "funnels"],
    levels: [4, 5],
    surfaces: {
      visible: ["browser"],
      managed: ["browser"],
    },
    enables: ["dashboard"],
    managedBy: "operator",
    healthCheck: "passive",
    lifecycle: "running",
    required: false,
    configModel: [
      { key: "project_api_key", label: "Project API key", type: "secret", required: true, operatorOnly: true },
      { key: "host", label: "Host", type: "string", required: false, default: "https://app.posthog.com", operatorOnly: true },
    ],
  },

  cloudflare: {
    source: "cloudflare",
    label: "Edge & DNS",
    description: "Cloudflare Workers, D1 databases, R2 storage, and DNS management. The substrate everything runs on.",
    category: "edge",
    icon: "△",
    services: ["workers", "d1", "r2", "dns", "cron"],
    levels: [1, 2, 3, 4, 5, 6],
    surfaces: {
      visible: ["browser"],
      managed: ["browser"],
    },
    enables: ["dashboard", "campaigns", "offers", "profiles", "conveyance"],
    managedBy: "operator",
    healthCheck: "cron",
    lifecycle: "running",
    required: true,
    configModel: [
      { key: "worker_route", label: "Worker route", type: "string", required: true, operatorOnly: true },
      { key: "d1_binding", label: "D1 database binding", type: "string", required: true, operatorOnly: true },
      { key: "r2_bucket", label: "R2 bucket", type: "string", required: false, operatorOnly: true },
      { key: "account_id", label: "Account ID", type: "string", required: true, operatorOnly: true },
      { key: "api_token", label: "API token", type: "secret", required: true, operatorOnly: true },
    ],
  },

  vercel: {
    source: "vercel",
    label: "Deployment",
    description: "Frontend deployment and hosting for the portal and landing pages. Where your workspace lives.",
    category: "deploy",
    icon: "▲",
    services: ["deploy", "hosting"],
    levels: [2, 3, 6],
    surfaces: {
      visible: ["browser"],
      managed: ["browser"],
    },
    enables: ["profiles"],
    managedBy: "operator",
    healthCheck: "passive",
    lifecycle: "running",
    required: true,
    configModel: [
      { key: "project_id", label: "Project ID", type: "string", required: true, operatorOnly: true },
      { key: "deployment_url", label: "Deployment URL", type: "string", required: true, operatorOnly: true },
      { key: "access_token", label: "Access token", type: "secret", required: true, operatorOnly: true },
    ],
  },
};

// ── Integration ordering — by dependency depth ────────────────────────────
// The order reflects the provisioning sequence: cloudflare first (substrate),
// then nim (needs cloudflare), then agentmail (needs cloudflare DNS), etc.
export const INTEGRATION_ORDER: IntegrationSource[] = [
  "cloudflare",   // substrate — everything depends on this
  "nim",          // AI brain — needs cloudflare
  "agentmail",    // email — needs cloudflare DNS
  "stripe",       // billing — independent but needed for offers
  "notion",       // knowledge — optional, client-managed
  "posthog",      // analytics — optional, operator-managed
  "vercel",       // deploy — operator-managed
];

// ── Alert categories from Fern ──────────────────────────────────────────────
export type AlertCategory =
  | "webhook_failed"
  | "cron_failed"
  | "send_failed"
  | "nim_failed"
  | "stripe_provisioning_failed"
  | "repeated_api_error";

export type AlertSeverity = "CRITICAL" | "WARNING" | "INFO";
export type AlertStatus = "OPEN" | "ACKNOWLEDGED" | "RESOLVED";

// ── Failure tracking — bespoke per integration ────────────────────────────
export interface IntegrationFailure {
  id: string;
  tenant_id?: string;
  source: IntegrationSource;
  operation: string;
  target_ref?: string;
  severity: AlertSeverity;
  status: AlertStatus;
  last_error?: string;
  attempts: number;
  last_attempt_at?: string;
  resolved_at?: string;
}

// ── Health state — bespoke per integration ──────────────────────────────────
export interface IntegrationHealth {
  source: IntegrationSource;
  status: IntegrationStatus;
  last_check?: string;
  uptime_pct?: number;
  error_count_24h?: number;
  // Bespoke health metrics per integration
  metrics?: Record<string, number | string>;
}

// ── Helper: map alert category to integration source ──────────────────────
export function alertToSource(category: AlertCategory): IntegrationSource | null {
  switch (category) {
    case "send_failed":
    case "webhook_failed":
      return "agentmail";
    case "nim_failed":
      return "nim";
    case "stripe_provisioning_failed":
      return "stripe";
    case "cron_failed":
      return "cloudflare";
    case "repeated_api_error":
      return null; // Could be any
    default:
      return null;
  }
}

// ── Helper: get integrations by workflow level ────────────────────────────
export function integrationsForLevel(level: WorkflowLevel): IntegrationSource[] {
  return INTEGRATION_ORDER.filter(source =>
    INTEGRATIONS[source].levels.includes(level)
  );
}

// ── Helper: get integrations visible on a surface ────────────────────────
export function integrationsForSurface(surface: SurfaceType): IntegrationSource[] {
  return INTEGRATION_ORDER.filter(source =>
    INTEGRATIONS[source].surfaces.visible.includes(surface)
  );
}

// ── Helper: get integrations managed by a role ───────────────────────────
export function integrationsByManager(role: "client" | "operator"): IntegrationSource[] {
  return INTEGRATION_ORDER.filter(source => {
    const m = INTEGRATIONS[source].managedBy;
    return m === role || m === "both";
  });
}

// ── Helper: get the provisioning sequence (dependency-ordered) ──────────
export function getProvisioningSequence(): { source: IntegrationSource; blocks: IntegrationSource[] }[] {
  const sequence: { source: IntegrationSource; blocks: IntegrationSource[] }[] = [];
  for (const source of INTEGRATION_ORDER) {
    const meta = INTEGRATIONS[source];
    const blocks = meta.dependsOn || [];
    sequence.push({ source, blocks });
  }
  return sequence;
}

// ── Helper: check if an integration is ready to provision ─────────────────
export function canProvision(
  source: IntegrationSource,
  activeIntegrations: Set<IntegrationSource>
): boolean {
  const deps = INTEGRATIONS[source].dependsOn || [];
  return deps.every(dep => activeIntegrations.has(dep));
}
